import AWS from 'aws-sdk';
import axios from 'axios';
import { S3_CONFIG, API_CONFIG, UPLOAD_CONFIG } from '../config/aws-config';

// Initialise S3 client
const s3 = new AWS.S3({
  region: S3_CONFIG.region,
  accessKeyId: S3_CONFIG.accessKeyId,
  secretAccessKey: S3_CONFIG.secretAccessKey
});

/**
 * Interface for S3 upload result
 */
export interface S3UploadResult {
  success: boolean;
  location?: string;
  key?: string;
  error?: string;
}

/**
 * Interface for API notification result
 */
export interface ApiNotificationResult {
  success: boolean;
  response?: any;
  error?: string;
}

/**
 * Uploads a file to AWS S3
 * @param file The file to upload
 * @param customPath Optional custom path/prefix for the file in S3
 * @returns Promise with the upload result
 */
export const uploadFileToS3 = async (
  file: File,
  customPath?: string
): Promise<S3UploadResult> => {
  try {
    // Generate a unique key for the file
    const timestamp = new Date().getTime();
    const fileExtension = file.name.split('.').pop();
    const folderPrefix = customPath || UPLOAD_CONFIG.s3FolderPrefix;
    const key = `${folderPrefix}${timestamp}-${file.name}`;

    // Set up the upload parameters
    const params = {
      Bucket: S3_CONFIG.bucketName,
      Key: key,
      Body: file,
      ContentType: file.type,
      // Make the file publicly accessible (optional - set to false for private files)
      ACL: 'public-read'
    };

    // Upload the file to S3
    const uploadResult = await s3.upload(params).promise();

    return {
      success: true,
      location: uploadResult.Location,
      key: uploadResult.Key
    };
  } catch (error) {
    console.error('Error uploading file to S3:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error during S3 upload'
    };
  }
};

/**
 * Notifies the API about the S3 upload
 * @param s3Location The S3 location/ARN of the uploaded file
 * @param fileMetadata Optional additional metadata about the file
 * @returns Promise with the API notification result
 */
export const notifyApiAboutUpload = async (
  s3Location: string,
  fileMetadata?: Record<string, any>
): Promise<ApiNotificationResult> => {
  try {
    // Set up headers if API key is provided
    const headers: Record<string, string> = {};
    if (API_CONFIG.apiKey) {
      headers['x-api-key'] = API_CONFIG.apiKey;
    }

    const response = await axios.post(
      API_CONFIG.uploadNotificationEndpoint, 
      {
        s3Location,
        fileType: 's3',
        ...fileMetadata
      },
      { headers }
    );

    return {
      success: true,
      response: response.data
    };
  } catch (error) {
    console.error('Error notifying API about upload:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error during API notification'
    };
  }
};

/**
 * Uploads a file to S3 and notifies the API in a single operation
 * @param file The file to upload
 * @param fileMetadata Optional additional metadata about the file
 * @returns Promise with both the S3 upload and API notification results
 */
export const uploadFileAndNotifyApi = async (
  file: File,
  fileMetadata?: Record<string, any>
): Promise<{
  s3Upload: S3UploadResult;
  apiNotification?: ApiNotificationResult;
}> => {
  // First upload the file to S3
  const s3UploadResult = await uploadFileToS3(file);

  // If S3 upload was successful, notify the API
  if (s3UploadResult.success && s3UploadResult.location) {
    const apiNotificationResult = await notifyApiAboutUpload(
      s3UploadResult.location,
      {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        uploadedAt: new Date().toISOString(),
        ...fileMetadata
      }
    );

    return {
      s3Upload: s3UploadResult,
      apiNotification: apiNotificationResult
    };
  }

  // If S3 upload failed, return only the S3 upload result
  return {
    s3Upload: s3UploadResult
  };
}; 